// src/core/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface StudentType {
  studentId: any;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActiveStudent: boolean;
  whatsappNumber: number;
}

interface AuthContextType {
  isAuthenticated: boolean;
  /** False until the first /api/auth/me answer; pages that need a login wait for it. */
  authChecked: boolean;
  student: StudentType | null;
  login: (email: string, password: string, from?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [student, setStudent] = useState<StudentType | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const redirectToDashboard = (role: string) => {
    switch (role.toUpperCase()) {
      case "ADMIN":
        navigate("/admin/dashboard");
        break;
      case "MENTOR":
        navigate("/mentor/dashboard");
        break;
      case "EMPLOYEE":
        navigate("/employee/dashboard");
        break;
      default:
        navigate("/student/student-dashboard");
    }
  };

  useEffect(() => {
    const fetchCurrentStudent = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
          {
            credentials: "include",
          }
        );

        if (res.status === 401) {
          setIsAuthenticated(false);
          setStudent(null);
          return;
        }

        if (res.ok) {
          const data: StudentType = await res.json();

          setStudent(data);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setStudent(null);
        }
      } catch (err) {
        setIsAuthenticated(false);
        setStudent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentStudent();
  }, []);

  const login = async (email: string, password: string, from?: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Login failed");
      }

      const userRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
        { credentials: "include" }
      );

      if (!userRes.ok) throw new Error("Failed to fetch user info");

      const data: StudentType = await userRes.json();

      setStudent(data);
      setIsAuthenticated(true);

      if (from) {
        navigate(from, { replace: true });
      } else {
        redirectToDashboard(data.role);
      }
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout error", err);
    }
    setIsAuthenticated(false);
    setStudent(null);
    navigate("/login");
  };

  // Public pages render right away; only protected routes wait for the login check
  // (blocking the whole site here made every page load flash a "Loading..." screen).
  return (
    <AuthContext.Provider value={{ isAuthenticated, authChecked: !loading, student, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
